/*  OrioAIHero — the Gemini-style block-frame at the top of Home.

    Ultra-clean layout (nothing wasted):
      ┌─────────────────────────────────┐
      │ [wordmark left]     [quota chip]│
      │                                 │
      │                                 │
      │          [ORIO O]    ←animated  │
      │             AI       gradient   │
      │                                 │
      │    [placeholder input bar]      │
      │                                 │
      │       Ciao, Marco 👋            │
      │                                 │
      └─────────────────────────────────┘

    When the user starts typing / sends a message, the whole area becomes
    the chat: the ORIO logo hides, the messages take its space, the input
    stays anchored, and the greeting slides out. Same central footprint,
    no page-jump — exactly like Gemini.

    Contextual placeholders: parent Home passes real user data (name, dog,
    weather, alerts, overdue reminders) and we mix generic prompts with
    hyper-specific ones like "Marco, hai 3 promemoria scaduti — vuoi che li
    apra?" so every interaction feels personal.
*/
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, X, ArrowUp, AlertTriangle, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACTION_LABELS = {
  open_profile:        { label: "Apri Profilo",         to: "/app/profilo" },
  open_billing:        { label: "Gestione Abbonamento", to: "/billing" },
  open_settings:       { label: "Impostazioni",         to: "/app/profilo" },
  open_reminders:      { label: "Apri Promemoria",      to: "/app/cassetto" },
  open_stories_new:    { label: "Nuova Storia",         to: "/app/match?tab=stories&new=1" },
  open_stories:        { label: "Apri Storie",          to: "/app/match?tab=stories" },
  open_match:          { label: "Apri Match",           to: "/app/match" },
  open_parkroom:       { label: "Park Room",            to: "/app/match?tab=parkroom" },
  open_corsi:          { label: "Corsi & Educatori",    to: "/app/match?tab=corsi" },
  open_sos:            { label: "SOS Emergenza",        to: "/app/mappa?tab=sos" },
  open_map:            { label: "Apri Mappa",           to: "/app/mappa" },
  open_missions:       { label: "Apri Missioni",        to: "/app/missioni" },
  open_shelter_apply:  { label: "Diventa Rifugio",      to: "/app/rifugio/registra" },
  open_drawer:         { label: "Cassetto digitale",    to: "/app/cassetto" },
  open_walking:        { label: "Apri ORIO Walk",       to: "/app/passeggiate" },
  open_veterinari:     { label: "Trova Veterinari",     to: "/app/mappa?tab=veterinari" },
  open_karma:          { label: "Il tuo Karma",         to: "/app/profilo#karma" },
  open_notifications:  { label: "Notifiche",            to: "/app/notifiche" },
  open_radar:          { label: "ORIO Radar",           to: "/app/mappa?tab=radar" },
};

const GENERIC_PLACEHOLDERS = [
  "Cosa può mangiare il mio cane oggi?",
  "Il mio cane trema, cosa faccio?",
  "Aggiungi un promemoria vaccino",
  "Come pubblico una storia?",
  "Che razze sono tranquille in appartamento?",
  "Cosa devo evitare in casa che è tossico?",
];

export default function OrioAIHero({ user, myDog, weather, radarSummary, reminders }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [quota, setQuota] = useState(null);
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // Dynamic ORIO-generated briefing shown above the input.
  const [briefing, setBriefing] = useState(null);
  // Voice — mic input (Web Speech Recognition) + auto-speak output (SpeechSynthesis)
  const [listening, setListening] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(() => {
    try { return localStorage.getItem("orio.ai.autospeak") === "1"; } catch { return false; }
  });
  const recognitionRef = useRef(null);
  const speakingRef = useRef(false);

  const firstName = useMemo(() => {
    const n = user?.name || "";
    return n.trim().split(/\s+/)[0] || "";
  }, [user?.name]);

  // Typewriter animation state — types the current placeholder char-by-char,
  // pauses, deletes, then moves to the next. Gives the input a "living",
  // Gemini-esque feel while the user isn't focused.
  const [typed, setTyped] = useState("");

  // Build contextual placeholder rotation using user data.
  const placeholders = useMemo(() => {
    const list = [];
    const dogName = myDog?.name;
    const alerts = radarSummary?.total || radarSummary?.count || 0;
    const overdue = (reminders || []).filter((r) => (r?._days ?? 0) < 0).length;
    const temp = weather?.temperature != null ? Math.round(weather.temperature) : null;

    if (firstName && alerts > 0) list.push(`${firstName}, hai ${alerts} allerte vicine — dimmi quali sono più urgenti…`);
    if (firstName && overdue > 0) list.push(`${firstName}, ${overdue} promemoria in ritardo — vuoi che li apra?`);
    if (dogName && temp != null && temp >= 26) list.push(`${temp}° oggi: quando è meglio uscire con ${dogName}?`);
    if (dogName && temp != null && temp <= 4) list.push(`${temp}° oggi: ${dogName} sente freddo?`);
    if (dogName) list.push(`Come faccio a stancare ${dogName} in 20 minuti?`);
    if (dogName) list.push(`${dogName} può mangiare la banana?`);
    if (firstName) list.push(`Ciao ${firstName}, chiedimi cosa vuoi sul tuo cane…`);
    // Always mix in some generic ones so it feels alive.
    return [...list, ...GENERIC_PLACEHOLDERS];
  }, [firstName, myDog?.name, weather?.temperature, radarSummary, reminders]);

  // Load quota on mount (used for the toast gate; UI badge removed)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [qRes, bRes] = await Promise.all([
          api.get("/orio-ai/quota").catch(() => ({ data: null })),
          api.get("/orio-ai/briefing").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setQuota(qRes?.data?.quota || null);
        setBriefing(bRes?.data || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  // Typewriter loop: types current phrase char by char, pauses 1.4s at the
  // end, deletes, moves to the next. Runs only when the input is idle and
  // the hero is not in chat mode.
  useEffect(() => {
    if (input || sending || expanded) return;
    if (!placeholders.length) return;
    let cancelled = false;
    let i = 0; // phrase index
    let charIdx = 0;
    let deleting = false;

    const tick = () => {
      if (cancelled) return;
      const phrase = placeholders[i % placeholders.length] || "";
      if (!deleting) {
        charIdx = Math.min(charIdx + 1, phrase.length);
        setTyped(phrase.slice(0, charIdx));
        if (charIdx === phrase.length) {
          // Pause at full phrase, then start deleting
          setTimeout(() => { if (!cancelled) { deleting = true; tick(); } }, 1600);
          return;
        }
        setTimeout(tick, 55 + Math.random() * 30);
      } else {
        charIdx = Math.max(charIdx - 1, 0);
        setTyped(phrase.slice(0, charIdx));
        if (charIdx === 0) {
          deleting = false;
          i += 1;
          setTimeout(tick, 260);
          return;
        }
        setTimeout(tick, 22 + Math.random() * 16);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [input, sending, expanded, placeholders]);

  // Auto-scroll chat on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, sending]);

  const sendAsk = async (text) => {
    const clean = (text || input).trim();
    if (!clean || sending) return;
    if (quota && !quota.is_unlimited && quota.used >= quota.limit) {
      toast.error("Hai finito le domande gratuite di questo mese. Passa a ORIO Pro 🐾");
      return;
    }
    setMessages((m) => [...m, { role: "user", text: clean }]);
    setInput("");
    setSending(true);
    setExpanded(true);

    const history = messages.slice(-6).map((m) => ({ role: m.role, text: m.text }));
    try {
      const { data } = await api.post("/orio-ai/ask", {
        message: clean, history, session_id: sessionId,
      });
      setSessionId(data.session_id);
      setQuota(data.quota);
      const newMsg = {
        role: "assistant", text: data.reply,
        action: data.action || null,
        emergency: !!data.emergency,
      };
      setMessages((m) => [...m, newMsg]);
      // Auto-speak the response if the user has opted in.
      if (autoSpeak) speak(data.reply);
    } catch (e) {
      const detail = e?.response?.data?.detail || "ORIO AI non risponde. Riprova.";
      setMessages((m) => [...m, { role: "assistant", text: detail, error: true }]);
      if (e?.response?.status === 402) {
        setQuota((q) => q ? { ...q, used: q.limit } : q);
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const closeChat = () => {
    setMessages([]);
    setSessionId(null);
    setExpanded(false);
    setInput("");
    stopSpeaking();
    stopListening();
  };

  // ─── Voice: SpeechSynthesis (TTS) ───────────────────────────────────
  // Speaks the given text with an Italian voice if available. Cheap +
  // native — no extra key needed, works on iOS Safari and Chrome Android.
  const speak = (text) => {
    if (!text) return;
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel(); // stop anything currently reading
      // Strip markdown asterisks + emoji clutter for a cleaner read.
      const clean = String(text)
        .replace(/\*\*/g, "")
        .replace(/[`*_~#>]/g, "")
        .replace(/```[\s\S]*?```/g, "")
        .trim();
      const utter = new window.SpeechSynthesisUtterance(clean);
      utter.lang = "it-IT";
      utter.rate = 1.02;
      utter.pitch = 1.0;
      // Prefer a native Italian voice if the system offers one.
      const voices = synth.getVoices();
      const it = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("it"));
      if (it) utter.voice = it;
      utter.onstart = () => { speakingRef.current = true; };
      utter.onend = () => { speakingRef.current = false; };
      utter.onerror = () => { speakingRef.current = false; };
      synth.speak(utter);
    } catch { /* silent — TTS is best-effort */ }
  };
  const stopSpeaking = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
    speakingRef.current = false;
  };
  const toggleAutoSpeak = () => {
    const next = !autoSpeak;
    setAutoSpeak(next);
    try { localStorage.setItem("orio.ai.autospeak", next ? "1" : "0"); } catch { /* silent */ }
    if (!next) stopSpeaking();
    toast(next ? "Voce attiva — ORIO leggerà le risposte" : "Voce disattivata");
  };

  // ─── Voice: SpeechRecognition (STT) ─────────────────────────────────
  // Dictates into the input box in it-IT. When the user pauses we send
  // the message automatically for a natural voice-first UX.
  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Dettatura vocale non supportata da questo browser");
      return;
    }
    if (listening) return;
    stopSpeaking(); // don't listen and speak at the same time
    const rec = new SR();
    rec.lang = "it-IT";
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    let finalText = "";
    rec.onstart = () => setListening(true);
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += chunk;
        else interim += chunk;
      }
      const combined = (finalText + interim).trim();
      if (combined) setInput(combined);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
        toast.error("Autorizza il microfono nelle impostazioni del browser");
      } else if (e?.error !== "aborted" && e?.error !== "no-speech") {
        toast.error("Errore microfono");
      }
    };
    rec.onend = () => {
      setListening(false);
      const clean = (finalText || "").trim();
      if (clean) {
        setInput(clean);
        // Fire the send after a short delay so the UI settles.
        setTimeout(() => sendAsk(clean), 200);
      }
    };
    recognitionRef.current = rec;
    try { rec.start(); } catch { /* silent */ }
  };
  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch { /* silent */ }
    setListening(false);
  };

  // Cleanup on unmount — cancel any speech & recognition still running.
  useEffect(() => {
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
      try { recognitionRef.current?.stop(); } catch { /* silent */ }
    };
  }, []);

  return (
    <section
      data-testid="orio-ai-hero"
      className="relative w-full overflow-hidden"
      style={{
        minHeight: "78vh",
        // Multi-stop ORIO gradient — warm cream at the top (blends with the
        // stories ribbon), then peach/amber/rose peeking through at ~30-60%,
        // then a deeper terracotta glow at the bottom that fades into the
        // page below. All animated halos sit on top for the shimmer.
        background:
          "linear-gradient(180deg, " +
          "#FBF7EF 0%, " +
          "#FBF1DE 22%, " +
          "#FDE7C6 45%, " +
          "#FFDAB0 65%, " +
          "#FFC996 82%, " +
          "#FFE1BE 100%)",
      }}
    >
      <MotionHalos />

      {/* Grid: center stage / input+greeting. No top row anymore — the
          mobile header above the hero already provides logo + icons. */}
      <div className="relative z-10 h-full flex flex-col"
           style={{
             minHeight: "78vh",
             paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)",
             paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 60px)",
           }}>
        {/* LOGO — sits a bit lower than before so it leaves more breathing
            room between the sticky Stories ribbon and the wordmark. */}
        {!expanded && (
          <div className="flex justify-center pt-32 pb-0"
               data-testid="orio-ai-logo-wrap">
            <div
              className="orio-wordmark-grad-hero"
              aria-label="ORIO"
              data-testid="orio-ai-logo"
              style={{ width: "min(58vw, 240px)", height: "min(17vw, 70px)", marginBottom: "-8px" }}
            />
          </div>
        )}

        {/* CENTER STAGE — chat area / greeting + input, always same footprint */}
        <div className="flex-1 min-h-0 flex flex-col justify-center px-5 max-w-md mx-auto w-full">
          {expanded && (
            <div
              ref={scrollRef}
              className="overflow-y-auto space-y-2.5 py-3 min-h-[46vh]"
              style={{ maxHeight: "56vh" }}
              data-testid="orio-ai-chat"
            >
              {messages.map((m, i) => (
                <ChatBubble key={i} m={m} onAction={(k) => {
                  const spec = ACTION_LABELS[k];
                  if (spec) navigate(spec.to);
                }} onSpeak={speak} />
              ))}
              {sending && (
                <div className="inline-flex items-center gap-2 text-[13px] text-[#7A7A7A] pl-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[#FF7A1A]" /> ORIO sta rispondendo…
                </div>
              )}
            </div>
          )}

          {/* DYNAMIC ORIO BRIEFING above the input — greeting + live facts.
              Centered so it feels part of the same visual unit as the input. */}
          {!expanded && (
            <div
              className="text-center mb-4 px-2 mx-auto"
              style={{ maxWidth: "92%" }}
              data-testid="orio-ai-greeting"
            >
              <div className="font-display text-[22px] sm:text-[24px] text-[#1A1A1A] leading-tight"
                   style={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
                {briefing?.greeting || (firstName ? `Ciao, ${firstName} 👋` : "Ciao 👋")}
              </div>
              {briefing?.briefing && (
                <div className="mt-1.5 text-[13.5px] text-[#5A5A5A] leading-snug"
                     data-testid="orio-ai-briefing">
                  {briefing.briefing}
                </div>
              )}
            </div>
          )}

          {/* Input bar — centered under greeting */}
          <div
            className="flex items-center gap-1.5 rounded-full p-1.5 pl-4 mx-auto w-full"
            style={{
              maxWidth: "94%",
              background: "white",
              border: listening ? "1px solid rgba(233,84,42,0.55)" : "1px solid rgba(255,122,26,0.25)",
              boxShadow: listening
                ? "0 16px 34px -14px rgba(233,84,42,0.55)"
                : "0 16px 34px -14px rgba(255,122,26,0.35)",
              transition: "border-color 200ms, box-shadow 200ms",
            }}
          >
            {/* Ghost placeholder (typed animation) shown BEHIND the input when
                empty. We keep the real input with an empty placeholder so the
                caret + focus behavior stays native. */}
            <div className="relative flex-1 h-10 min-w-0">
              {!input && !expanded && !listening && (
                <div
                  aria-hidden
                  className="absolute inset-0 flex items-center pointer-events-none text-[14px] text-[#B5AE9E] whitespace-nowrap overflow-hidden pr-2"
                  data-testid="orio-ai-typewriter"
                >
                  <span className="truncate">{typed}</span>
                  <span
                    className="inline-block ml-[1px] h-[16px] w-[1.5px] bg-[#B5AE9E]"
                    style={{ animation: "orio-caret-blink 1s steps(2) infinite" }}
                  />
                </div>
              )}
              {!input && listening && (
                <div
                  aria-hidden
                  className="absolute inset-0 flex items-center pointer-events-none text-[14px] text-[#E9542A] whitespace-nowrap overflow-hidden pr-2"
                  data-testid="orio-ai-listening-hint"
                >
                  <span className="truncate italic">In ascolto…</span>
                </div>
              )}
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendAsk(); }}
                placeholder={expanded && !listening ? "Continua la conversazione…" : ""}
                data-testid="orio-ai-input"
                maxLength={800}
                className="absolute inset-0 w-full h-full bg-transparent outline-none text-[14px] text-[#1A1A1A] placeholder-[#B5AE9E]"
              />
            </div>
            {/* Mic — voice dictation. Pulses red while listening. */}
            <button
              onClick={() => (listening ? stopListening() : startListening())}
              aria-label={listening ? "Ferma dettatura" : "Dettatura vocale"}
              data-testid="orio-ai-mic"
              className="h-10 w-10 rounded-full flex items-center justify-center active:scale-95 shrink-0"
              style={{
                background: listening
                  ? "linear-gradient(135deg, #E9542A 0%, #FF3B1A 100%)"
                  : "rgba(255,122,26,0.10)",
                color: listening ? "#fff" : "#B5601D",
                boxShadow: listening ? "0 10px 20px -8px rgba(233,84,42,0.55)" : "none",
                transition: "background 200ms, box-shadow 200ms",
              }}
            >
              {listening ? (
                <MicOff className="h-4 w-4" strokeWidth={2.4} />
              ) : (
                <Mic className="h-4 w-4" strokeWidth={2.4} />
              )}
            </button>
            {expanded && (
              <button
                onClick={closeChat}
                aria-label="Chiudi"
                data-testid="orio-ai-close"
                className="h-10 w-10 rounded-full flex items-center justify-center text-[#7A7A7A] active:scale-95 shrink-0"
                style={{ background: "rgba(255,122,26,0.10)" }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => sendAsk()}
              disabled={!input.trim() || sending}
              data-testid="orio-ai-send"
              className="h-10 w-10 rounded-full flex items-center justify-center text-white active:scale-95 shrink-0"
              style={{
                background: input.trim() ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" : "#E8DDCB",
                boxShadow: input.trim() ? "0 10px 20px -8px rgba(255,122,26,0.55)" : "none",
                opacity: sending ? 0.6 : 1,
              }}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" strokeWidth={2.6} />}
            </button>
          </div>

          {/* Auto-speak toggle — small pill below the input. Persists in
              localStorage so users don't have to re-enable it each launch. */}
          {!expanded && (
            <div className="flex items-center justify-center mt-3">
              <button
                onClick={toggleAutoSpeak}
                aria-label="Attiva o disattiva la lettura vocale"
                data-testid="orio-ai-autospeak"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold active:scale-95"
                style={{
                  background: autoSpeak ? "rgba(255,122,26,0.14)" : "rgba(255,255,255,0.6)",
                  color: autoSpeak ? "#B5601D" : "#7A7A7A",
                  border: `1px solid ${autoSpeak ? "rgba(255,122,26,0.35)" : "rgba(0,0,0,0.06)"}`,
                  backdropFilter: "blur(6px)",
                }}
              >
                {autoSpeak ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                {autoSpeak ? "Voce attiva" : "Attiva voce"}
              </button>
            </div>
          )}

          {!expanded && (
            <div className="text-center mt-4 text-[11px] text-[#9A9A9A] font-medium tracking-wide">
              Scorri giù ↓
            </div>
          )}
        </div>
      </div>

      {/* Bottom fade — blends the hero into the next section (Stories strip)
          so there's no hard cut between the two visual worlds. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0"
        style={{
          height: "120px",
          background: "linear-gradient(180deg, rgba(255,225,190,0) 0%, rgba(251,247,239,0.6) 55%, #FBF7EF 100%)",
        }}
      />
    </section>
  );
}

/* AnimatedOLogo removed — replaced by the CSS-only .orio-wordmark-grad-hero
   which draws the full ORIO wordmark with a continuous animated gradient. */

function MotionHalos() {
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Orange primary — top-left, biggest */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 500, height: 500, top: "-160px", left: "-140px",
          background: "radial-gradient(circle, rgba(255,122,26,0.48) 0%, rgba(255,122,26,0) 70%)",
          filter: "blur(30px)",
        }}
        animate={{ x: [0, 40, -20, 0], y: [0, 30, -10, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Amber — top-right */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 420, height: 420, top: "12%", right: "-150px",
          background: "radial-gradient(circle, rgba(244,184,60,0.48) 0%, rgba(244,184,60,0) 70%)",
          filter: "blur(28px)",
        }}
        animate={{ x: [0, -40, 20, 0], y: [0, -30, 10, 0] }}
        transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Rose — mid-left, brand-new brand accent */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 340, height: 340, top: "38%", left: "-100px",
          background: "radial-gradient(circle, rgba(255,150,120,0.42) 0%, rgba(255,150,120,0) 70%)",
          filter: "blur(26px)",
        }}
        animate={{ x: [0, 22, -18, 0], y: [0, 18, -14, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Terracotta deep — bottom center */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 400, height: 400, bottom: "-140px", left: "30%",
          background: "radial-gradient(circle, rgba(233,84,42,0.40) 0%, rgba(233,84,42,0) 70%)",
          filter: "blur(30px)",
        }}
        animate={{ x: [0, 30, -25, 0], y: [0, 20, -10, 0] }}
        transition={{ duration: 19, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Sage green whisper — bottom-right, adds ORIO nature */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 300, height: 300, bottom: "6%", right: "-80px",
          background: "radial-gradient(circle, rgba(151,192,141,0.32) 0%, rgba(151,192,141,0) 70%)",
          filter: "blur(24px)",
        }}
        animate={{ x: [0, -18, 12, 0], y: [0, -14, 10, 0] }}
        transition={{ duration: 21, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Peach shimmer center — subtle pulse behind the logo */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 320, height: 320, top: "42%", left: "38%",
          background: "radial-gradient(circle, rgba(255,203,148,0.42) 0%, rgba(255,203,148,0) 70%)",
          filter: "blur(22px)",
        }}
        animate={{ opacity: [0.5, 0.95, 0.5], scale: [1, 1.06, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Soft cream bloom top-center — pulls the logo forward */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 260, height: 260, top: "24%", left: "36%",
          background: "radial-gradient(circle, rgba(253,232,206,0.55) 0%, rgba(253,232,206,0) 70%)",
          filter: "blur(20px)",
        }}
        animate={{ opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

function ChatBubble({ m, onAction, onSpeak }) {
  const isUser = m.role === "user";
  const isAssistant = m.role === "assistant";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div className="max-w-[85%]">
        {m.emergency && isAssistant && (
          <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold text-[#E9542A] uppercase tracking-widest mb-1">
            <AlertTriangle className="h-3 w-3" /> Emergenza
          </div>
        )}
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-line ${
            isUser
              ? "text-white rounded-br-sm"
              : m.error
              ? "bg-[#FFF0EA] text-[#B75E2A] rounded-bl-sm border border-[#FFDCC5]"
              : "text-[#1A1A1A] rounded-bl-sm"
          }`}
          style={{
            background: isUser
              ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)"
              : m.error
              ? undefined
              : "rgba(255,255,255,0.9)",
            boxShadow: isUser
              ? "0 10px 22px -12px rgba(255,122,26,0.55)"
              : m.error
              ? undefined
              : "0 6px 14px -10px rgba(0,0,0,0.08)",
            border: isUser ? "none" : m.error ? undefined : "1px solid rgba(240,235,227,0.9)",
          }}
        >
          {m.text}
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {m.action && ACTION_LABELS[m.action.type] && (
            <button
              onClick={() => onAction(m.action.type)}
              data-testid={`orio-ai-action-${m.action.type}`}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-bold text-white active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #E9542A 100%)",
                boxShadow: "0 10px 22px -10px rgba(255,122,26,0.55)",
              }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {ACTION_LABELS[m.action.type].label}
            </button>
          )}
          {isAssistant && !m.error && onSpeak && (
            <button
              onClick={() => onSpeak(m.text)}
              aria-label="Ascolta la risposta"
              data-testid="orio-ai-speak"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full active:scale-95"
              style={{
                background: "rgba(255,122,26,0.10)",
                color: "#B5601D",
                border: "1px solid rgba(255,122,26,0.22)",
              }}
            >
              <Volume2 className="h-3.5 w-3.5" strokeWidth={2.4} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
